import React from "react";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  MenuItem,
  Stack,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  PictureAsPdf,
  Description,
  TableChart,
  Slideshow,
  Image,
  FolderZip,
  InsertDriveFile,
  Download,
  Edit,
  DriveFileMove,
  Delete,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import PermissionGate from "../../../components/common/PermissionGate";
import { DOCUMENT_PERMISSIONS } from "../../../utils/documentPermissions";

// ─── File type icon mapping ───

const MIME_ICON_MAP = {
  "application/pdf": PictureAsPdf,
  "application/msword": Description,
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": Description,
  "application/vnd.ms-excel": TableChart,
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": TableChart,
  "application/vnd.ms-powerpoint": Slideshow,
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": Slideshow,
  "image/jpeg": Image,
  "image/jpg": Image,
  "image/png": Image,
  "application/zip": FolderZip,
  "application/x-zip-compressed": FolderZip,
};

const getFileIcon = (mimeType) => {
  const IconComponent = MIME_ICON_MAP[mimeType] || InsertDriveFile;
  return <IconComponent fontSize="small" sx={{ color: "action.active", mr: 1 }} />;
};

// ─── Formatters ───

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "—";
  if (bytes === 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = (bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0);
  return `${size} ${units[i]}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return dayjs(dateStr).format("DD MMM YYYY, hh:mm A");
};

// ─── Filter options ───

const FORMAT_OPTIONS = [
  { value: "", label: "All Formats" },
  { value: "application/pdf", label: "PDF" },
  { value: "application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document", label: "Word" },
  { value: "application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", label: "Excel" },
  { value: "application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation", label: "PowerPoint" },
  { value: "image/jpeg,image/jpg,image/png", label: "Images" },
  { value: "application/zip,application/x-zip-compressed", label: "ZIP" },
];

/**
 * DocumentList — Paginated file list with sorting, filtering, and actions.
 *
 * Props:
 * - documents: Array of document objects
 * - pagination: { page, pageSize, totalItems, totalPages }
 * - loading: boolean
 * - sortModel: Array of { field, sort } for DataGrid
 * - onSortModelChange: (model) => void
 * - onPageChange: (page, pageSize) => void
 * - onFilterChange: (filters) => void
 * - onDocumentAction: (action, document) => void
 */
const DocumentList = ({
  documents = [],
  pagination = {},
  loading = false,
  sortModel = [],
  onSortModelChange,
  onPageChange,
  onFilterChange,
  onDocumentAction,
}) => {
  const [filters, setFilters] = React.useState({
    format: "",
    uploader: "",
    dateFrom: null,
    dateTo: null,
    tag: "",
  });

  const handleFilterChange = (field, value) => {
    const updated = { ...filters, [field]: value };
    setFilters(updated);

    // Build filter payload for parent
    const filterPayload = {};
    if (updated.format) filterPayload.mimeType = updated.format;
    if (updated.uploader) filterPayload.uploader = updated.uploader;
    if (updated.dateFrom) filterPayload.dateFrom = dayjs(updated.dateFrom).format("YYYY-MM-DD");
    if (updated.dateTo) filterPayload.dateTo = dayjs(updated.dateTo).format("YYYY-MM-DD");
    if (updated.tag) filterPayload.tag = updated.tag;

    if (onFilterChange) {
      onFilterChange(filterPayload);
    }
  };

  // ─── Column definitions ───

  const columns = [
    {
      field: "title",
      headerName: "Title",
      flex: 1.5,
      minWidth: 200,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
          {getFileIcon(params.row?.mimeType)}
          <Typography variant="body2" noWrap title={params.value}>
            {params.value || "Untitled"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "uploaderName",
      headerName: "Uploaded By",
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => {
        const isInactive = params.row?.uploaderStatus === "inactive";
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="body2" noWrap>
              {params.value || "Unknown"}
            </Typography>
            {isInactive && (
              <Typography
                variant="caption"
                sx={{ color: "error.main", fontWeight: 500, whiteSpace: "nowrap" }}
              >
                (Inactive)
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: "fileSize",
      headerName: "Size",
      width: 100,
      sortable: true,
      renderCell: (params) => (
        <Typography variant="body2">{formatFileSize(params.value)}</Typography>
      ),
    },
    {
      field: "uploadDate",
      headerName: "Upload Date",
      width: 180,
      sortable: true,
      renderCell: (params) => (
        <Typography variant="body2">{formatDate(params.value)}</Typography>
      ),
    },
    {
      field: "tags",
      headerName: "Tags",
      flex: 1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => {
        const tags = params.value || [];
        if (!tags.length) return <Typography variant="body2" color="text.secondary">—</Typography>;
        return (
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
            {tags.slice(0, 3).map((tag) => (
              <Chip key={tag} label={tag} size="small" variant="outlined" />
            ))}
            {tags.length > 3 && (
              <Typography variant="caption" color="text.secondary">
                +{tags.length - 3}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 180,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <PermissionGate permission={DOCUMENT_PERMISSIONS.VIEW}>
            <Tooltip title="Download">
              <IconButton
                size="small"
                onClick={() => onDocumentAction?.("download", params.row)}
              >
                <Download fontSize="small" />
              </IconButton>
            </Tooltip>
          </PermissionGate>
          <PermissionGate permission={DOCUMENT_PERMISSIONS.EDIT}>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => onDocumentAction?.("edit", params.row)}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          </PermissionGate>
          <PermissionGate permission={DOCUMENT_PERMISSIONS.EDIT}>
            <Tooltip title="Move">
              <IconButton
                size="small"
                onClick={() => onDocumentAction?.("move", params.row)}
              >
                <DriveFileMove fontSize="small" />
              </IconButton>
            </Tooltip>
          </PermissionGate>
          <PermissionGate permission={DOCUMENT_PERMISSIONS.DELETE}>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={() => onDocumentAction?.("delete", params.row)}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </PermissionGate>
        </Box>
      ),
    },
  ];

  // ─── Empty state ───

  if (!loading && (!documents || documents.length === 0)) {
    return (
      <Box>
        {/* Filters row */}
        <FilterBar
          filters={filters}
          onFilterChange={handleFilterChange}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
          }}
        >
          <InsertDriveFile sx={{ fontSize: 48, color: "action.disabled", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No documents found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Upload files or adjust your filters to see documents here.
          </Typography>
        </Box>
      </Box>
    );
  }

  // ─── Main render ───

  const { totalItems = 0, page = 1, pageSize = 20 } = pagination;

  return (
    <Box>
      {/* Filters row */}
      <FilterBar
        filters={filters}
        onFilterChange={handleFilterChange}
      />

      {/* DataGrid */}
      <Box sx={{ width: "100%", mt: 2 }}>
        <DataGrid
          rows={documents}
          columns={columns}
          loading={loading}
          getRowId={(row) => row.id || row.uuid}
          // Server-side pagination
          pagination
          paginationMode="server"
          rowCount={totalItems}
          page={Math.max(0, page - 1)}
          pageSize={pageSize}
          onPageChange={(newPage) => onPageChange?.(newPage + 1, pageSize)}
          onPageSizeChange={(newSize) => onPageChange?.(1, newSize)}
          rowsPerPageOptions={[10, 20, 50, 100]}
          // Server-side sorting
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(model) => onSortModelChange?.(model)}
          // Appearance
          autoHeight
          disableSelectionOnClick
          disableColumnFilter
          rowHeight={56}
          sx={{
            border: "none",
            "& .MuiDataGrid-cell": { borderBottom: "1px solid #f0f0f0" },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f5f5f5",
              fontWeight: 600,
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#fafafa",
            },
          }}
        />
      </Box>
    </Box>
  );
};

// ─── Filter Bar Sub-Component ───

const FilterBar = ({ filters, onFilterChange }) => (
  <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
    <TextField
      select
      size="small"
      label="Format"
      value={filters.format}
      onChange={(e) => onFilterChange("format", e.target.value)}
      sx={{ minWidth: 140 }}
    >
      {FORMAT_OPTIONS.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </TextField>

    <TextField
      size="small"
      label="Uploader"
      value={filters.uploader}
      onChange={(e) => onFilterChange("uploader", e.target.value)}
      sx={{ minWidth: 140 }}
      placeholder="Filter by name"
    />

    <DatePicker
      label="From"
      value={filters.dateFrom}
      onChange={(val) => onFilterChange("dateFrom", val)}
      slotProps={{ textField: { size: "small", sx: { minWidth: 140 } } }}
    />

    <DatePicker
      label="To"
      value={filters.dateTo}
      onChange={(val) => onFilterChange("dateTo", val)}
      slotProps={{ textField: { size: "small", sx: { minWidth: 140 } } }}
    />

    <TextField
      size="small"
      label="Tag"
      value={filters.tag}
      onChange={(e) => onFilterChange("tag", e.target.value)}
      sx={{ minWidth: 120 }}
      placeholder="Filter by tag"
    />
  </Stack>
);

export default DocumentList;
